import { Box } from '@wix/design-system';
import { FunctionComponent, useContext, useEffect, useMemo } from 'react';
import { DATA_HOOKS } from '../../../dataHooks';
import {
  ArticleSearchResultItem,
  SearchResultsByType,
  NormalizedResource,
  SearchResults,
  SearchResultItem,
  ArticleSourceType,
  PageType,
  ExternalResourceType,
} from '../../../types';
import css from './index.module.scss';
import { ResultsResourceGroup } from './ResultsResourceGroup';
import { EXPERIMENTS, LOCALES } from '../../../constants';
import { useExperiments } from '@wix/fe-essentials-standalone';
import {
  articleSearchResultToSearchResultItem,
  normalizedResourceToSearchResultItem,
} from '../../../utils/externalSearch';
import classNames from 'classnames';
import { ResultsFooter } from './ResultsFooter';
import { ArticleType } from '@wix/answers-api';
import { Context } from '../../../context';
import { wixStudioUserFilters } from '../../SearchResults/ExternalSearchFilter';
import { useRouter } from 'next/router';

export const ARTICLE_GROUP_SIZE = 5;
export const OTHER_GROUP_SIZE = 3;

export type SearchBarResultsProps = {
  articles: ArticleSearchResultItem[];
  externalSearchResults?: SearchResultsByType[];
  inputText: string;
  focusedArticleIndex: number;
  onSearchResultClick: (
    url: string,
    type: ArticleSourceType,
    index: number,
    id?: string
  ) => Promise<void>;
  onClickAllResults: () => Promise<void>;
  onClickShowMore: (type: ArticleSourceType) => Promise<void>;
  setFocusedArticleIndex: (index: number) => void;
  setFocusedResult: (result: SearchResultItem) => void;
  totalDisplayedResultsCount: number;
  setTotalDisplayedResultsCount: (count: number) => void;
  resultsListRef: React.RefObject<HTMLDivElement>;
  pageType: PageType;
  searchSessionId: string;
};

export const SearchBarResults: FunctionComponent<SearchBarResultsProps> = ({
  articles,
  externalSearchResults,
  inputText,
  focusedArticleIndex,
  onSearchResultClick,
  onClickAllResults,
  onClickShowMore,
  setFocusedArticleIndex,
  setFocusedResult,
  totalDisplayedResultsCount,
  setTotalDisplayedResultsCount,
  resultsListRef,
  pageType,
  searchSessionId
}) => {
  const { experiments } = useExperiments({ suspense: true, readOnly: true });
  const { locale } = useRouter();
  const isExternalSearchResultsEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.EXTERNAL_SEARCH
  ) && locale === LOCALES.EN;

  const { isWixStudioUser } = useContext(Context);
  const showResourceToUser = (resourceType: ExternalResourceType) =>
    (isWixStudioUser && wixStudioUserFilters.indexOf(resourceType) !== -1) || !isWixStudioUser;

  const groupByTypeSearchResults = (): SearchResults => {
    const typedResults: SearchResults = {};

    articles.forEach((article) => {
      const key = article.type;
      if (typedResults[key]) {
        typedResults[key].items.push(
          articleSearchResultToSearchResultItem(article)
        );
      } else {
        typedResults[key] = {
          items: [articleSearchResultToSearchResultItem(article)],
          itemsCount: articles.filter((a) => a.type === key).length,
        };
      }
    });

    externalSearchResults?.forEach((externalResult) => {
      const { resourceType } = externalResult;
      if (showResourceToUser(resourceType)) {
        typedResults[resourceType] = {
          items: externalResult.items.map(
            (resource: ArticleSearchResultItem | NormalizedResource) =>
              normalizedResourceToSearchResultItem(
                resource as NormalizedResource
              )
          ),
          itemsCount: externalResult.itemsCount,
        };
      }
    });

    return typedResults;
  };

  const buildGroupPrefixCountArray = () => {
    const groupCountArray: number[] = [0];

    for (const [key, group] of Object.entries(resultsByType)) {
      const groupLimit =
        key === ArticleType.ARTICLE.toString()
          ? ARTICLE_GROUP_SIZE
          : OTHER_GROUP_SIZE;
      const actualCount = Math.min(groupLimit, group.items.length);
      const previous = groupCountArray[groupCountArray.length - 1];
      groupCountArray.push(actualCount + previous);
    }
    return groupCountArray;
  };

  useEffect(() => {
    setTotalDisplayedResultsCount(groupPrefixCountArray.at(-1) || 0);
  }, [articles, externalSearchResults]);

  const resultsByType = useMemo(
    () => groupByTypeSearchResults(),
    [articles, externalSearchResults]
  );

  const groupPrefixCountArray = useMemo(
    () => buildGroupPrefixCountArray(),
    [articles, externalSearchResults]
  );

  const getGroupSize = (type: string) =>
    type === ArticleType.ARTICLE.toString()
      ? ARTICLE_GROUP_SIZE
      : OTHER_GROUP_SIZE;

  const areResultsPresent =
    (articles && articles.length > 0) ||
    (isExternalSearchResultsEnabled &&
      externalSearchResults &&
      externalSearchResults.length > 0);

  const isShowMoreVisible = (type: ArticleSourceType) => {
    const isTypeInternal =
      Object.values(ArticleType).indexOf(type as ArticleType) !== -1;
    return (
      isTypeInternal ||
      resultsByType[type].itemsCount > getGroupSize(type.toString())
    );
  };

  return (
    <Box
      ref={resultsListRef}
      display={areResultsPresent ? 'block' : 'none'}
      className={classNames(css.searchBarResults, {
        [css.externalSearchEnabled]: isExternalSearchResultsEnabled,
      })}
      dataHook={DATA_HOOKS.SEARCH_BAR_RESULTS}
      direction="vertical"
    >
      {isExternalSearchResultsEnabled ? (
        <>
          {Object.values(resultsByType).map((sameTypeResults, index) => (
            <ResultsResourceGroup
              key={index}
              searchResults={sameTypeResults.items.slice(
                0,
                getGroupSize(sameTypeResults.items[0].type.toString())
              )}
              inputText={inputText}
              focusedArticleIndex={focusedArticleIndex}
              prefixSize={groupPrefixCountArray[index]}
              onSearchResultClick={onSearchResultClick}
              setFocusedArticleIndex={setFocusedArticleIndex}
              isShowMoreVisible={isShowMoreVisible(
                sameTypeResults.items[0].type
              )}
              setFocusedResult={setFocusedResult}
              onShowMoreClick={onClickShowMore}
              pageType={pageType}
              searchSessionId={searchSessionId}
            ></ResultsResourceGroup>
          ))}
        </>
      ) : (
        <>
          <ResultsResourceGroup
            key={(articles[0] as ArticleSearchResultItem).type}
            searchResults={articles as ArticleSearchResultItem[]}
            inputText={inputText}
            focusedArticleIndex={focusedArticleIndex}
            prefixSize={0}
            onSearchResultClick={onSearchResultClick}
            setFocusedArticleIndex={setFocusedArticleIndex}
            pageType={pageType}
            searchSessionId={searchSessionId}
          ></ResultsResourceGroup>
        </>
      )}
      <ResultsFooter
        inputText={inputText}
        resultsCount={
          !isExternalSearchResultsEnabled
            ? articles?.length ?? 0
            : totalDisplayedResultsCount
        }
        onClickAllResults={onClickAllResults}
        focusedArticleIndex={focusedArticleIndex}
      />
    </Box>
  );
};
